import { Box, Paper, TableContainer, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import Chip from '../../components/Chip/Chip';
import DefaultTable from '../../components/DefaultTable/DefaultTable';
import TableToolbar from '../../components/DefaultTable/TableToolbar/TableToolbar';
import PageTitle from '../../components/PageTitle/PageTitle';
import { HeadCell } from '../../models/headCell';
import { useTableFunctions } from '../../hooks/useTableFunctions';
import { reportService } from '../../services/reportService';
import { format } from 'date-fns';
import FormatCurrency from '../../components/FormatNumber/FormatCurrency';
import { textEllipsis } from '../../constants';
import { Report } from '../../models/report';
import {
  createDownloadFile,
  formatAMPM,
  removeEmptyObjectFields
} from '../../utils/helpers';
import { Pagination } from '../../models/pagination';
import GlobalSearch from '../../components/GlobalSearch/GlobalSearch';
import { useShowFilterOrSearch } from '../../hooks/useShowFilterOrSearch';
import FilterReports, { FilterReportsForm } from './FilterReports';
import { ReportStatus } from '../../enums/reportStatus';
import { ReportPaymentCategories } from '../../enums/reportPaymentCategories';
import { getRoundStatusStyle } from '../../utils/statusRoundStyle';

const initialReportsColumnsData: HeadCell[] = [
  {
    id: 'id',
    label: 'ID',
    sortable: true,
    visible: false,
    render: (report: Report) => report.id
  },
  {
    id: 'createdOn',
    label: 'Date',
    sortable: true,
    visible: false,
    thStyle: { width: 100, textAlign: 'center' },
    tdStyle: { textAlign: 'center' },
    render: (report: Report) =>
      report.createdOn ? format(new Date(report.createdOn), 'MM/dd/yy') : null
  },
  {
    id: 'talent.firstName',
    label: 'Talent',
    sortable: true,
    visible: true,
    render: (report: Report) =>
      report.talentUser.firstName + ' ' + report.talentUser.lastName
  },
  {
    id: 'description',
    label: 'Description',
    sortable: true,
    visible: true,
    thStyle: { width: 180 },
    render: (report: Report) =>
      report.description && (
        <Box sx={{ ...textEllipsis, WebkitLineClamp: 3, maxWidth: 200 }}>
          {report.description}
        </Box>
      )
  },
  {
    id: 'address',
    label: 'Location',
    sortable: true,
    visible: true,
    thStyle: { maxWidth: 200 },
    tdStyle: { maxWidth: 200 },
    render: (report: Report) => report.address
  },
  {
    id: 'paymentCategory',
    label: 'Payment Category',
    sortable: true,
    visible: true,
    render: (report: Report) =>
      ReportPaymentCategories[
        report.paymentCategory as keyof typeof ReportPaymentCategories
      ]
  },
  {
    id: 'amountPaid',
    label: 'Total Pay',
    sortable: true,
    visible: true,
    render: (report: Report) =>
      report.amountPaid && <FormatCurrency value={report.amountPaid} />
  },
  {
    id: 'client',
    label: 'Client',
    sortable: true,
    visible: true,
    render: (report: Report) => report.client
  },
  {
    id: 'jobOrder',
    label: 'Job/Order #',
    sortable: true,
    visible: true,
    render: (report: Report) => report.jobOrder
  },
  {
    id: 'timeIn',
    label: 'Time In',
    sortable: true,
    visible: true,
    thStyle: { width: 100 },
    render: (report: Report) => formatAMPM(report.timeIn)
  },
  {
    id: 'timeOut',
    label: 'Time Out',
    sortable: true,
    visible: true,
    thStyle: { width: 100 },
    render: (report: Report) => formatAMPM(report.timeOut)
  },
  {
    id: 'onsiteTime',
    label: 'Onsite Time',
    sortable: true,
    visible: true,
    thStyle: { width: 100, textAlign: 'center' },
    tdStyle: { textAlign: 'center' },
    render: (report: Report) => report.onsiteTime
  },
  {
    id: 'breakTime',
    label: 'Breaks',
    sortable: true,
    visible: true,
    thStyle: { width: 100, textAlign: 'center' },
    tdStyle: { textAlign: 'center' },
    render: (report: Report) => report.breakTime
  },
  {
    id: 'hoursPaid',
    label: 'H. Paid',
    sortable: true,
    visible: true,
    thStyle: { width: 100, textAlign: 'center' },
    tdStyle: { textAlign: 'center' },
    render: (report: Report) => report.hoursPaid
  },
  {
    id: 'pricePerHour',
    label: 'Rate',
    sortable: true,
    visible: true,
    thStyle: { width: 100, textAlign: 'center' },
    tdStyle: { textAlign: 'center' },
    render: (report: Report) =>
      report.pricePerHour && <FormatCurrency value={report.pricePerHour} />
  },
  {
    id: 'status',
    label: 'Status',
    sortable: true,
    visible: true,
    thStyle: { width: 150, textAlign: 'center', minWidth: 170 },
    tdStyle: { textAlign: 'center' },
    render: (report: Report) =>
      report.status && (
        <Box
          sx={{
            ...getRoundStatusStyle(ReportStatus[report.status])
          }}
        >
          {ReportStatus[report.status]}
        </Box>
      )
  },
  {
    id: 'projectTitle',
    label: 'Project Title',
    sortable: true,
    visible: false,
    thStyle: { width: 100, textAlign: 'center' },
    tdStyle: { textAlign: 'center' },
    render: (report: Report) => report.projectTitle
  },
  {
    id: 'manager.firstName',
    label: 'Manager',
    sortable: true,
    visible: false,
    thStyle: { width: 100, textAlign: 'center' },
    tdStyle: { textAlign: 'center' },
    render: (report: Report) => report.managerId
  },
  {
    id: 'company',
    label: 'Company',
    sortable: true,
    visible: false,
    thStyle: { width: 100, textAlign: 'center' },
    tdStyle: { textAlign: 'center' },
    render: (report: Report) => report.company
  },
  {
    id: 'createdBy',
    label: 'Created by',
    sortable: true,
    visible: false,
    thStyle: { width: 100, textAlign: 'center' },
    tdStyle: { textAlign: 'center' },
    render: (report: Report) => report.createdBy
  },
  {
    id: 'modifiedDate',
    label: 'Modified on',
    sortable: true,
    visible: false,
    thStyle: { width: 100, textAlign: 'center' },
    tdStyle: { textAlign: 'center' },
    render: (report: Report) =>
      report.modifiedDate
        ? format(new Date(report.modifiedDate), 'MM/dd/yy')
        : null
  },
  {
    id: 'paymentType',
    label: 'Payment Type',
    sortable: true,
    visible: false,
    thStyle: { width: 100, textAlign: 'center' },
    tdStyle: { textAlign: 'center' },
    render: (report: Report) => report.paymentType
  },
  {
    id: 'amountOpen',
    label: 'Amount Open',
    sortable: true,
    visible: false,
    thStyle: { width: 100, textAlign: 'center' },
    tdStyle: { textAlign: 'center' },
    render: (report: Report) =>
      report.amountOpen && <FormatCurrency value={report.amountOpen} />
  },
  {
    id: 'paymentDate',
    label: 'Payment Date',
    sortable: true,
    visible: false,
    thStyle: { width: 100, textAlign: 'center' },
    tdStyle: { textAlign: 'center' },
    render: (report: Report) =>
      report.paymentDate
        ? format(new Date(report.paymentDate), 'MM/dd/yy')
        : null
  },
  {
    id: 'quantity',
    label: 'Quantity',
    sortable: true,
    visible: false,
    thStyle: { width: 100, textAlign: 'center' },
    tdStyle: { textAlign: 'center' },
    render: (report: Report) => report.quantity
  },
  {
    id: 'invoice',
    label: 'Invoice',
    sortable: true,
    visible: false,
    thStyle: { width: 100, textAlign: 'center' },
    tdStyle: { textAlign: 'center' },
    render: (report: Report) => report.invoice
  },
  {
    id: 'talentNotes',
    label: 'Talent Notes',
    sortable: true,
    visible: false,
    thStyle: { width: 100, textAlign: 'center' },
    tdStyle: { textAlign: 'center' },
    render: (report: Report) => report.talentNotes
  },
  {
    id: 'rejectNotes',
    label: 'Reject Notes',
    sortable: true,
    visible: false,
    thStyle: { width: 100, textAlign: 'center' },
    tdStyle: { textAlign: 'center' },
    render: (report: Report) => report.rejectNotes
  },
  {
    id: 'expenses',
    label: 'Expenses',
    sortable: true,
    visible: false,
    thStyle: { width: 100, textAlign: 'center' },
    tdStyle: { textAlign: 'center' },
    render: (report: Report) =>
      report.expenses && <FormatCurrency value={report.expenses} />
  },
  {
    id: 'specialInstruction',
    label: 'Special Instruction',
    sortable: true,
    visible: false,
    thStyle: { width: 100, textAlign: 'center' },
    tdStyle: { textAlign: 'center' },
    render: (report: Report) => report.specialInstructions
  },
  {
    id: 'entity',
    label: 'Entity',
    sortable: true,
    visible: false,
    thStyle: { width: 100, textAlign: 'center' },
    tdStyle: { textAlign: 'center' },
    render: (report: Report) => report.entity
  },
  {
    id: 'expenseCode',
    label: 'Expense Code',
    sortable: true,
    visible: false,
    thStyle: { width: 100, textAlign: 'center' },
    tdStyle: { textAlign: 'center' },
    render: (report: Report) => report.expenseCode
  }
];

const ReportsList = (): JSX.Element => {
  const initialFilterData: FilterReportsForm = {
    talentId: [],
    startDate: null,
    endDate: null,
    paymentCategories: [],
    status: [],
    companyName: null
  };
  const [paginationReports, setPaginationReports] =
    useState<Pagination<Report>>();
  const [loading, setLoading] = useState<boolean>(true);
  const [showFilters, showSearch, onFilterClick, onSearchClick] =
    useShowFilterOrSearch();
  const {
    columnsData,
    setColumnsData,
    page,
    rowsPerPage,
    sortBy,
    sortType,
    searchTerm,
    filterData,
    onRequestSort,
    onChangePage,
    onChangeRowsPerPage,
    onSearchChange,
    onFilterChange,
    onChangeColumnVisibility
  } = useTableFunctions<FilterReportsForm>(
    initialReportsColumnsData,
    initialFilterData
  );
  useEffect(() => {
    getReports();
  }, [page, rowsPerPage, sortBy, sortType, filterData, searchTerm]);

  const getReports = () => {
    reportService
      .getReports(generateFilterBody(), {
        params: {
          page: page,
          size: rowsPerPage,
          sort: `${sortBy},${sortType}`,
          searchTerm
        }
      })
      .then((response) => {
        setPaginationReports(response.data);
        setLoading(false);
      });
  };

  const handleExport = () => {
    reportService.exportReport(generateFilterBody()).then((response) => {
      createDownloadFile(response.data, 'Allwork report');
    });
  };

  useEffect(() => {
    const draftItems = localStorage.getItem('columnsReport');
    if (draftItems != null) {
      const aux = JSON.parse(draftItems);
      const test = initialReportsColumnsData.map((element, index) => ({
        ...element,
        visible: aux[index]
      }));
      setColumnsData(test);
    }
  }, [loading]);

  const handleColumnsData =
    (column: HeadCell) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const newColumnData = onChangeColumnVisibility(column)(event);
      const picked = newColumnData.map((e) => e.visible);
      const aux = JSON.stringify(picked);
      localStorage.setItem('columnsReport', aux);
    };

  const generateFilterBody = () => {
    let body = removeEmptyObjectFields(filterData);
    if (body.talentId) {
      body = {
        ...body,
        talentId: filterData.talentId
          ? filterData.talentId.map((talent) => talent.id)
          : []
      };
    }
    if (body.companyName) {
      body = {
        ...body,
        companyName: filterData.companyName ? filterData.companyName.name : []
      };
    }
    if (searchTerm) {
      body = {
        ...body,
        searchTerm: searchTerm
      };
    }
    return body;
  };

  return (
    <>
      <Box display="flex" alignItems="center">
        <PageTitle>Reporting</PageTitle>
        <Chip size={paginationReports?.totalElements} />
      </Box>
      <Box>
        <TableContainer component={Paper} sx={{ minHeight: '320px' }}>
          <TableToolbar
            viewTableColumnsProps={{
              columns: columnsData,
              onColumnClick: handleColumnsData
            }}
            onFilter={onFilterClick}
            onSearch={onSearchClick}
            onExport={handleExport}
          >
            {showSearch && (
              <GlobalSearch
                onSearchChange={onSearchChange}
                searchTerm={searchTerm}
              />
            )}
            {showFilters && (
              <FilterReports
                handleFormSubmit={onFilterChange}
                filterData={filterData}
              />
            )}
          </TableToolbar>
          <DefaultTable
            columns={columnsData}
            data={paginationReports?.content}
            sortProps={{ sortBy, sortType, onRequestSort }}
            paginationProps={{
              page,
              rowsPerPage,
              onChangePage,
              onChangeRowsPerPage,
              totalElements: paginationReports?.totalElements
            }}
          />
          {!loading && !paginationReports?.content.length && (
            <Typography variant="h5" align="center" m={4}>
              No Reports found!
            </Typography>
          )}
        </TableContainer>
      </Box>
    </>
  );
};

export default ReportsList;
